var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * Resolves the mailing address to pre-fill the edit form from a publisher update request.
 *
 * The update-request `data` JSON only carries `mailingAddress` when it actually changed, so we
 * must distinguish three states to build the form value and reflect the pending change correctly:
 *   (a) key absent      -> mailing unchanged   -> keep the publisher's current address
 *   (b) value === null  -> pending REMOVAL      -> no address (empty form, approve removes it)
 *   (c) value is object -> change / addition    -> merge current address with the pending values
 *
 * Distinguishing "key absent" (undefined) from an explicit `null` is essential: `null` is a pending
 * deletion, and pre-filling the original address there would silently drop the removal on approval.
 *
 * @param publisher the original (baseline) publisher record loaded from the API
 * @param data the pending-changes JSON of the update request (a partial {@link Publisher})
 * @returns the mailing address the form should show, or `undefined` when it is (being) removed
 */
export var resolvePendingMailingAddress = function (publisher, data) {
    var pendingData = data;
    if (!pendingData || !("mailingAddress" in pendingData)) {
        return publisher === null || publisher === void 0 ? void 0 : publisher.mailingAddress;
    }
    var pending = pendingData.mailingAddress;
    if (pending === null || pending === undefined) {
        return undefined;
    }
    return __assign(__assign({}, publisher === null || publisher === void 0 ? void 0 : publisher.mailingAddress), pending);
};
/**
 * Determines whether the pending change is an explicit removal of an existing mailing address —
 * the update-request data carries `mailingAddress: null` and the publisher currently has one.
 *
 * @param publisher the original (baseline) publisher record loaded from the API
 * @param data the pending-changes JSON of the update request (a partial {@link Publisher})
 * @returns `true` when an existing mailing address is being removed by the pending change
 */
export var isMailingAddressRemoval = function (publisher, data) {
    var pendingData = data;
    return (!!pendingData &&
        "mailingAddress" in pendingData &&
        pendingData.mailingAddress === null &&
        !!(publisher === null || publisher === void 0 ? void 0 : publisher.mailingAddress));
};
